import { useMemo } from "react";

import {
    BdsButton,
    BdsIconEyeglasses,
    BdsIconSlidersHorizontal,
    BdsText,
} from "@buildertrend/components";
import { type RefType } from "@buildertrend/components/src/lib/types/RefType";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { isNullOrUndefined } from "utilities/object/object";
import { routes } from "utilities/routes";

import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";

import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { SubSetupTabKeys } from "entity/sub/SubSetup/SubSetup.api.types";

import "./NotificationPanelHeader.less";

interface INotificationPanelHeaderProps {
    selectedJobId: number;
    isLoading: boolean;
    onMarkAllReadAsync: () => Promise<void>;
    focusButtonRef?: React.Ref<HTMLButtonElement>;
}

export function NotificationPanelHeader({
    selectedJobId,
    isLoading,
    onMarkAllReadAsync,
    focusButtonRef,
}: Readonly<INotificationPanelHeaderProps>) {
    const userInfo = useRequiredContext(UserInfoContext);

    const preferencesLink = useMemo(() => {
        if (isNullOrUndefined(userInfo.globalUserId)) {
            return null;
        }

        if (userInfo.loginType === BTLoginTypes.BUILDER) {
            return routes.internalUsers.getInternalUserDetailsLink(
                userInfo.globalUserId,
                InternalUserTabKeys.Notifications,
            );
        } else if (userInfo.loginType === BTLoginTypes.SUBS) {
            return routes.sub.getSetupLink(userInfo.globalUserId, SubSetupTabKeys.Notifications);
        } else if (userInfo.loginType === BTLoginTypes.CONTACT) {
            return routes.owner.getSetupLink(selectedJobId);
        }
        return null;
    }, [userInfo.globalUserId, userInfo.loginType, selectedJobId]);

    return (
        <BTRow
            data-testid="PanelTitle"
            className="NotificationPanelHeader"
            justify="center"
            align="middle"
        >
            <BdsText text="Notifications" size="distinct-lg" className="flex-grow-1" />
            <BTPopover content="Mark all read" data-testid="markAllReadTooltip">
                <div className="margin-right-xs">
                    <BdsButton
                        testid="markAllRead"
                        ariaLabel="Mark all read"
                        icon={<BdsIconEyeglasses />}
                        onClick={onMarkAllReadAsync}
                        disabled={isLoading}
                        className="MarkAllReadButton HeaderButton"
                        displayType="tertiary"
                        innerRef={focusButtonRef as unknown as RefType}
                    />
                </div>
            </BTPopover>

            <BTPopover content="Preferences" data-testid="preferencesTooltip">
                <BTLinkRelative
                    to={`/NotificationPanel${preferencesLink}`}
                    className="NotificationLink"
                    isUnderline={false}
                >
                    <BdsButton
                        testid="preferences"
                        displayType="tertiary"
                        ariaLabel="Preferences"
                        icon={<BdsIconSlidersHorizontal />}
                        disabled={isLoading}
                        className="HeaderButton"
                    />
                </BTLinkRelative>
            </BTPopover>
        </BTRow>
    );
}
